import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { SAFE_TYPE_ENUM } from "@antipartido-inc/contracts-ifaces"
import { SafePendingTransactionList } from "../../domain/safe/Models/SafePendingTransactionList"
import type { ExecutePendingSafeTransactionsResult } from "../../domain/safe/Models/ExecutePendingSafeTransactionsResult"
import { SafeTransactionList } from "../../domain/safe/Models/SafeTransactionList"

interface SafeTransactionsState {
  votingPendingTransactions?: SafePendingTransactionList
  votingPendingTransactionsIsLoading: boolean
  votingPendingTransactionsError?: string
  censusPendingTransactions?: SafePendingTransactionList
  censusPendingTransactionsIsLoading: boolean
  censusPendingTransactionsError?: string
  safeTransactions?: SafeTransactionList
  safeTransactionsIsLoading: boolean
  safeTransactionsError?: string
}

interface SafeTransactionsActions {
  fetchVotingPendingTransactions: () => Promise<SafePendingTransactionList | undefined>
  fetchCensusPendingTransactions: () => Promise<SafePendingTransactionList | undefined>
  fetchSafeTransactions: () => Promise<SafeTransactionList | undefined>
  executePendingSafeTransactions: () => Promise<ExecutePendingSafeTransactionsResult>
  reset: () => void
}

type SafeTransactionsStore = SafeTransactionsState & SafeTransactionsActions

const initialState: SafeTransactionsState = {
  votingPendingTransactions: undefined,
  votingPendingTransactionsIsLoading: false,
  votingPendingTransactionsError: undefined,
  censusPendingTransactions: undefined,
  censusPendingTransactionsIsLoading: false,
  censusPendingTransactionsError: undefined,
  safeTransactions: undefined,
  safeTransactionsIsLoading: false,
  safeTransactionsError: undefined,
}

export const useSafeTransactionsStore = create<SafeTransactionsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchVotingPendingTransactions: async () => {
        const state = get()
        if (state.votingPendingTransactionsIsLoading) return undefined

        set({ votingPendingTransactionsIsLoading: true, votingPendingTransactionsError: undefined })

        try {
          const { pendingTransactions } = await window.domain.GetPendingTransactionsSafeUseCase.execute({
            safeType: SAFE_TYPE_ENUM.VOTING,
          })
          set({ votingPendingTransactions: pendingTransactions, votingPendingTransactionsIsLoading: false })
          return pendingTransactions
        } catch (error) {
          set({ votingPendingTransactionsError: (error as Error).message, votingPendingTransactionsIsLoading: false })
          throw error
        }
      },

      fetchCensusPendingTransactions: async () => {
        const state = get()
        if (state.censusPendingTransactionsIsLoading) return undefined

        set({ censusPendingTransactionsIsLoading: true, censusPendingTransactionsError: undefined })

        try {
          const { pendingTransactions } = await window.domain.GetPendingTransactionsSafeUseCase.execute({
            safeType: SAFE_TYPE_ENUM.CENSUS,
          })
          set({ censusPendingTransactions: pendingTransactions, censusPendingTransactionsIsLoading: false })
          return pendingTransactions
        } catch (error) {
          set({ censusPendingTransactionsError: (error as Error).message, censusPendingTransactionsIsLoading: false })
          throw error
        }
      },

      fetchSafeTransactions: async () => {
        const state = get()
        if (state.safeTransactionsIsLoading) return undefined

        set({ safeTransactionsIsLoading: true, safeTransactionsError: undefined })

        try {
          const { safeTransactions } = await window.domain.GetSafeTransactionsSafeUseCase.execute()
          set({ safeTransactions, safeTransactionsIsLoading: false })
          return safeTransactions
        } catch (error) {
          set({ safeTransactionsError: (error as Error).message, safeTransactionsIsLoading: false })
          throw error
        }
      },

      executePendingSafeTransactions: async () => {
        const { executionResult } = await window.domain.ExecutePendingSafeTransactionsSafeUseCase.execute()
        set({ ...initialState })
        return executionResult
      },

      reset: () => {
        set({ ...initialState })
      },
    }),
    { name: "SafeTransactionsStore" },
  ),
)
